/**
 * TypeScript definitions for Electron IPC methods
 * This file extends the Window interface with Electron-specific APIs
 */

declare global {
  interface Window {
    electron?: {
      ipcRenderer: {
        send: (channel: string, data: any) => void;
        on: (channel: string, callback: (...args: any[]) => void) => void;
        once: (channel: string, callback: (...args: any[]) => void) => void;
        removeListener: (channel: string, callback: (...args: any[]) => void) => void;
        invoke: (channel: string, data?: any) => Promise<any>;
      };
    };
    electronAppContext?: {
      isStandalone?: boolean;
      appRoute?: string;
      appType?: string;
    };
  }
}

// DirWatcher IPC channels and types
export interface DirWatcherIPCResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface DirectoryTreeData {
  tree: {
    [path: string]: {
      path: string;
      name: string;
      isFolder: boolean;
      isFile: boolean;
      childrenList: string[];
      size?: number;
      modified?: string;
      hasChildren?: boolean;
    };
  };
}

export interface FileMetadataData {
  metadata: {
    path: string;
    name: string;
    extension: string;
    size: number;
    isDirectory: boolean;
    isFile: boolean;
    created: string;
    modified: string;
    accessed: string;
  };
}

export interface DeviceInfoData {
  username: string;
  os: 'MacOS' | 'Windows' | 'Linux' | 'Unknown';
  homedir: string;
}

export interface DirEventData {
  directory: string;
  file: string;
  action: 0 | 1 | 2 | 3; // CREATED, DELETED, MODIFIED, RENAMED
  path: string;
  newDirectory?: string;
  newFile?: string;
  oldFile?: string;
}

// Type-safe IPC invoke helpers
export const dirWatcherIPC = {
  init: (path: string) => 
    window.electron?.ipcRenderer.invoke('dirwatcher:init', { path }) as Promise<DirWatcherIPCResponse | undefined>,
    
  getTree: (paths: string[], maxDepth?: number) =>
    window.electron?.ipcRenderer.invoke('dirwatcher:get-tree', { paths, maxDepth }) as Promise<DirWatcherIPCResponse<DirectoryTreeData> | undefined>,
    
  getMetadata: (path: string) =>
    window.electron?.ipcRenderer.invoke('dirwatcher:get-metadata', { path }) as Promise<DirWatcherIPCResponse<FileMetadataData> | undefined>,
    
  kill: (path: string) =>
    window.electron?.ipcRenderer.invoke('dirwatcher:kill', { path }) as Promise<DirWatcherIPCResponse | undefined>,
    
  getDeviceInfo: () =>
    window.electron?.ipcRenderer.invoke('dirwatcher:device-info') as Promise<DirWatcherIPCResponse<DeviceInfoData> | undefined>,
    
  onEvent: (callback: (event: any, data: { type: string; events: DirEventData[] }) => void) =>
    window.electron?.ipcRenderer.on('dirwatcher:event', callback),
    
  removeEventListener: (callback: (event: any, data: { type: string; events: DirEventData[] }) => void) =>
    window.electron?.ipcRenderer.removeListener('dirwatcher:event', callback),
};

export {};

